import React from 'react'

import { CONDITION_DROPDOWN, LANGUAGE_DROPDOWN } from 'types/collection'
import { CollectionSpecificFilters } from 'redux/collectionV2/types'

import { useAppSelector } from 'redux/hooks'

import Select from 'components/formElements/SemanticSelect'
import LabelDisplay from 'components/collectionV2/misc/LabelDisplay'

import styles from './collectionFilterOptions.module.scss'

type Props = {
  filters: CollectionSpecificFilters
  onChange: (filters: CollectionSpecificFilters) => void
}

const TREATMENT_OPTIONS = [
  { key: 'any', value: undefined, text: 'Any' },
  { key: 'nonfoil', value: 'nonfoil', text: 'Nonfoil' },
  { key: 'foil', value: 'foil', text: 'Foil' },
]

const LOGIC_OPTIONS = [
  { key: '=', value: undefined, text: 'equals' },
  { key: '>', value: 'gt', text: 'greater than' },
  { key: '<', value: 'lt', text: 'less than' },
  { key: '>=', value: 'gte', text: 'greater than or equals' },
  { key: '<=', value: 'lte', text: 'less than or equals' },
]

const CollectionFilterOptions: React.FC<Props> = ({ filters, onChange }: Props) => {
  const tags = useAppSelector(state => state.collectionV2.tags)
  const collectionTags = Object.values(tags)
    .map(tag => ({
      key: tag.id,
      value: tag.id,
      text: tag.name,
      content: <LabelDisplay name={tag.name} color={tag.color} />,
    }))
    .sort((a, b) => (a.text < b.text ? -1 : 1))

  const handleChange = (value: any, key: string) => onChange({ ...filters, [key]: value })

  return (
    <div className={styles.container}>
      <h3>Collection specific filters</h3>

      <Select
        label="AND collection labels"
        placeholder="label_one and label_two"
        onChange={(value: string[]) => handleChange(value, 'andTags')}
        options={collectionTags}
        value={filters.andTags}
        multiple
        searchable
      />

      <Select
        label="OR collection labels"
        placeholder="label_one or label_two"
        onChange={(value: string[]) => handleChange(value, 'orTags')}
        options={collectionTags}
        value={filters.orTags}
        multiple
        searchable
      />

      <Select
        label="NOT collection labels"
        placeholder="not label_three"
        onChange={(value: string[]) => handleChange(value, 'notTags')}
        options={collectionTags}
        value={filters.notTags}
        multiple
        searchable
      />

      <div className={styles.spacer} />

      <Select
        label="Condition"
        placeholder="Near mint"
        onChange={(value: string[]) => handleChange(value, 'condition')}
        options={CONDITION_DROPDOWN}
        value={filters.condition}
        multiple
      />

      <Select
        label="Language"
        placeholder="English"
        onChange={(value: string[]) => handleChange(value, 'collectionLanguage')}
        options={LANGUAGE_DROPDOWN}
        value={filters.collectionLanguage}
        multiple
      />

      <Select
        label="Treatment"
        placeholder="Foil, Normal, Etched..."
        onChange={(value: string[]) => handleChange(value, 'treatment')}
        options={TREATMENT_OPTIONS}
        value={filters.treatment || null}
      />

      <div className={styles.quantityContainer}>
        <Select
          label="Quantity"
          placeholder="equals"
          onChange={(value: string[]) => handleChange(value, 'quantityLogic')}
          options={LOGIC_OPTIONS}
          value={filters.quantityLogic || null}
        />
        <input
          placeholder="1"
          type="number"
          value={filters.quantity}
          onChange={e => handleChange(e.target.value, 'quantity')}
        />
      </div>
    </div>
  )
}

export default CollectionFilterOptions
